import { Add, Send } from '@mui/icons-material'
import { BEIGE_500, GRAY_200, GRAY_500, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { ChangeEvent, FC, KeyboardEvent, useRef, useState } from 'react'

import { AttachmentTray } from '../Attachments/AttachmentTray'
import Box from '@mui/material/Box'
import { ComposerAttachments } from '../helpers/useChatAttachments'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined'
import Stack from '@mui/material/Stack'
import { requestNotificationPermission } from '../helpers/useBrowserNotifications'
import { useTranslation } from 'react-i18next'

interface Props {
  placeholder: string
  /** The composer must not be used at all: field and buttons are disabled. */
  disabled?: boolean
  /**
   * A send is in flight: submit is locked, but the field stays enabled and keeps focus so the next message
   * can be typed right away. A disabled field would drop focus and cost the client a click per message.
   */
  submitting?: boolean
  /** Focus the field when the composer mounts (a thread opened, the drawer expanded). */
  autoFocus?: boolean
  /**
   * Called when the user submits via the send icon or pressing Enter (without shift).
   * May return a promise: the composer clears optimistically and puts the draft back if it rejects,
   * so a failed send doesn't cost the user their message.
   */
  onSubmit: (text: string) => void | Promise<unknown>
  /** Called on each keystroke with non-empty content — used to emit the typing indicator (throttled by the caller). */
  onTyping?: () => void
  /** Called when the user picks "Add media from the property" from the attach menu. */
  onAttachFromProperty?: () => void
  onAttachFromComputer?: (files: File[]) => void
  attachDisabled?: boolean
  attachments?: ComposerAttachments
  /** Accessible label for the send icon button. */
  sendAriaLabel: string
  /** Accessible label for the attach icon button. */
  attachAriaLabel: string
}

/**
 * Composer used at the bottom of the chatting view. Renders an attach button that opens a
 * menu (property library / local upload) only when an attach handler is provided, the text field,
 * and a circular send button.
 * Empty input and a send in flight disable submit. Enter submits, Shift+Enter inserts a newline. Focus
 * stays in the field across a send, whichever way it was triggered.
 */
const MESSAGE_MAX_LENGTH = 10_000

export const MessageInput: FC<Props> = ({
  placeholder,
  disabled = false,
  submitting = false,
  autoFocus = false,
  onSubmit,
  onTyping,
  onAttachFromProperty,
  onAttachFromComputer,
  attachDisabled = false,
  attachments,
  sendAriaLabel,
  attachAriaLabel,
}) => {
  const { t } = useTranslation(['chat'])
  const [value, setValue] = useState('')
  const [attachAnchor, setAttachAnchor] = useState<null | HTMLElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const canAttach = Boolean(onAttachFromProperty || onAttachFromComputer)
  const opensPickerDirectly = Boolean(onAttachFromComputer && !onAttachFromProperty)
  const trimmed = value.trim()
  const canSubmit = !disabled && !submitting && (!attachments || attachments.canSend) &&
    (trimmed.length > 0 || !!attachments?.items.length) && trimmed.length <= MESSAGE_MAX_LENGTH

  const handleSend = () => {
    if (!canSubmit) return
    // Sending is a user gesture too, so it can carry the permission prompt; a no-op once asked this session.
    requestNotificationPermission('send')
    setValue('')
    // Clear optimistically so the composer feels instant, but restore the draft if the send rejects —
    // retrying shouldn't mean retyping. Never clobber something the user typed while it was in flight.
    void Promise.resolve(onSubmit(trimmed)).catch(() => setValue((current) => current || trimmed))
    // The send icon takes focus when clicked; give it back so the next message can be typed at once.
    inputRef.current?.focus()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSend()
    }
  }

  const openFilePicker = () => fileInputRef.current?.click()

  const handleFilesPicked = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''
    if (files.length) onAttachFromComputer?.(files)
  }

  const openAttachMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (opensPickerDirectly) {
      openFilePicker()
      return
    }
    setAttachAnchor(event.currentTarget)
  }
  const closeAttachMenu = () => setAttachAnchor(null)

  const handlePickProperty = () => {
    closeAttachMenu()
    onAttachFromProperty?.()
  }

  const handlePickComputer = () => {
    closeAttachMenu()
    openFilePicker()
  }

  return (
    <Box>

      {attachments &&
        <AttachmentTray attachments={attachments} disabled={disabled} />
      }

      <Stack
        direction="row"
        alignItems="center"
        gap="1rem"
        sx={{
          padding: '1.2rem 1.6rem',
          backgroundColor: WHITE,
        }}
      >

        {onAttachFromComputer &&
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            data-testid="chat-attachment-input"
            onChange={handleFilesPicked}
          />
        }

        {canAttach &&
          <IconButton
            size="small"
            aria-label={attachAriaLabel}
            aria-haspopup={opensPickerDirectly ? undefined : 'menu'}
            aria-expanded={opensPickerDirectly ? undefined : Boolean(attachAnchor)}
            disabled={disabled || attachDisabled}
            onClick={openAttachMenu}
            sx={{
              width: '4.4rem',
              height: '4.4rem',
              backgroundColor: BEIGE_500,
              color: GRAY_900,
              flexShrink: 0,
              '&:hover': { backgroundColor: BEIGE_500, opacity: 0.85 },
            }}
          >
            <Add sx={{ fontSize: '2.2rem' }} />
          </IconButton>
        }

        {canAttach && !opensPickerDirectly &&
          <Menu
            anchorEl={attachAnchor}
            open={Boolean(attachAnchor)}
            onClose={closeAttachMenu}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            slotProps={{
              paper: {
                elevation: 4,
                sx: { minWidth: '24rem', borderRadius: '0.8rem', marginTop: '-0.4rem' },
              },
            }}
          >
            {onAttachFromProperty &&
              <MenuItem onClick={handlePickProperty} sx={{ paddingY: '1rem' }}>
                <ListItemIcon>
                  <PhotoLibraryOutlinedIcon sx={{ fontSize: '2rem', color: GRAY_700 }} />
                </ListItemIcon>
                <ListItemText primary={t('chat:attach.from_property')} sx={{ color: GRAY_900 }} />
              </MenuItem>
            }
            {onAttachFromComputer &&
              <MenuItem onClick={handlePickComputer} sx={{ paddingY: '1rem' }}>
                <ListItemIcon>
                  <CloudUploadOutlinedIcon sx={{ fontSize: '2rem', color: GRAY_700 }} />
                </ListItemIcon>
                <ListItemText primary={t('chat:attach.from_computer')} sx={{ color: GRAY_900 }} />
              </MenuItem>
            }
          </Menu>
        }

        <Box
          sx={{
            flex: 1,
            minHeight: '4.4rem',
            padding: '0.4rem 1.6rem',
            backgroundColor: WHITE,
            border: `1px solid ${GRAY_200}`,
            borderRadius: '999px',
            display: 'flex',
            alignItems: 'center',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <InputBase
            fullWidth
            inputRef={inputRef}
            autoFocus={autoFocus}
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            multiline
            minRows={1}
            maxRows={4}
            onChange={(event) => {
              const next = event.target.value.slice(0, MESSAGE_MAX_LENGTH)
              setValue(next)
              if (next.trim().length > 0) onTyping?.()
            }}
            onKeyDown={handleKeyDown}
            sx={{
              fontSize: '1.5rem',
              color: GRAY_900,
              // Vertically center the (multiline) textarea so the placeholder/text lines up with the
              // round attach/send buttons — MUI's multiline root aligns to baseline by default.
              display: 'flex',
              alignItems: 'center',
              // Override the project's `inputs.sass` (`html[bkbn-style="old"] textarea { min-height: 10rem }`).
              // We prefix our selector with the SAME attribute so it always wins on specificity — without
              // this the legacy 10rem min-height keeps the textarea tall and the text sticks to the top.
              // NB: do NOT force `height: auto` here — that disables MUI's TextareaAutosize inline height
              // and the textarea falls back to its default `rows` (≈2 lines). We only kill the legacy
              // `min-height: 10rem` and let MUI size it (1 line via minRows, capped by maxRows).
              'html[bkbn-style="old"] && textarea, && .MuiInputBase-input, && textarea': {
                border: 'none !important',
                padding: '0 !important',
                minHeight: '0 !important',
                minWidth: '0 !important',
                maxWidth: '100% !important',
                backgroundColor: 'transparent !important',
                boxShadow: 'none !important',
                transition: 'none !important',
              },
              '&& .MuiInputBase-input:focus, && textarea:focus': {
                borderColor: 'transparent !important',
                boxShadow: 'none !important',
              },
              '&& input::placeholder, && textarea::placeholder': {
                color: GRAY_500,
                opacity: 1,
              },
            }}
          />
        </Box>

        <IconButton
          size="small"
          aria-label={sendAriaLabel}
          disabled={!canSubmit}
          onClick={handleSend}
          sx={{
            width: '4.4rem',
            height: '4.4rem',
            backgroundColor: GRAY_900,
            color: WHITE,
            flexShrink: 0,
            '&:hover': { backgroundColor: GRAY_900, opacity: 0.92 },
            // Keep the button dark when disabled (per design); only dim the icon slightly so the
            // affordance stays consistent with the rest of the composer.
            '&.Mui-disabled': { backgroundColor: GRAY_900, color: WHITE, opacity: 0.5 },
          }}
        >
          <Send sx={{ fontSize: '2.2rem', transform: 'translateX(0.1rem)' }} />
        </IconButton>

      </Stack>

    </Box>
  )
}
